<!-- THE ACTION MONITOR'S RUN VIEW (XCT-E-12-05) — the panel's body, moved here as it was so two hosts can render it:
     the Action Monitor's own panel, and the floating Xact Playground while its window is up. -->
@if (run) {
  <!-- Title bar — and, in either host, a drag handle for the window it sits in. Left out where the host draws its own
       bar over the run (XAT-E-377-02: the run window's Execution | Chat tabs, which carry the state and the count). -->
  @if (!titleless) {
  <div class="am-title" [class.am-title--embedded]="embedded" cdkDragHandle>
    @if (!embedded) {
      <i class="fa-solid fa-grip-vertical am-grip" aria-hidden="true"></i>
    }
    <div class="am-title-text">
      <!-- In the floating Xact Playground's drawer the title IS the state (review, 2026-09-26): Running, Finished. -->
      <div class="am-title-name">{{ embedded ? stateWord : 'ACTION MONITOR' }}</div>
      <div class="am-title-context">{{ run.context || run.title }}</div>
    </div>
    <div class="am-live"
         [class.running]="isRunning"
         [class.ok]="run.status === 'completed'"
         [class.bad]="run.status === 'failed'">
      <span class="am-dot"
            role="img"
            [attr.aria-label]="statusLabel"
            [class.running]="isRunning"
            [class.ok]="run.status === 'completed'"
            [class.bad]="run.status === 'failed'"
            [class.halted]="run.status === 'stopped' || run.status === 'timed-out'"></span>
      <span class="am-live-count">{{ counter }}</span>
      <span class="am-live-sep">·</span>
      <span class="am-live-elapsed">{{ elapsed }}</span>
    </div>
    <div class="am-title-actions">
      @if (canOpenFullResult) {
        <button type="button" class="am-tbtn" (click)="openFullResult()"
                title="Open the full result" aria-label="Open the full result">
          <i class="fa-solid fa-up-right-and-down-left-from-center" aria-hidden="true"></i>
        </button>
      }
      <!-- THE HOST'S OWN CHROME (XCT-E-12-05): the panel's Minimise and Close, or the float's dismiss. -->
      <ng-content select="[runViewChrome]"></ng-content>
    </div>
  </div>
  }

  <!-- Progress -->
  <div class="am-progress">
    <div class="am-progress-fill"
         [class.running]="isRunning"
         [class.ok]="run.status === 'completed'"
         [class.bad]="run.status === 'failed'"
         [style.width.%]="progressPercent"></div>
  </div>

  <!-- Rows, grouped by the batch they were dispatched in (XAT-E-253-03). A failed row leads with what
       it EXPECTED and carries the engine's own text underneath; the steps the run never reached are
       listed, dimmed, rather than absent — a run cut short mid-way has to read as one. -->
  <div class="am-steps" #stepList (scroll)="onStepsScrolled()">
    @if (reader) {
      <!-- THE READER'S LIST (XAT-E-526-03): a chat run, one list numbered 1…N — the status, the step's plain caption and
           its time, and under a failed step one plain line instead of the engine's message. -->
      @for (step of run.steps; track step.key; let index = $index) {
        <div class="am-step am-step--reader"
             [class.dim]="isDim(step)"
             [class.am-step--running]="step.status === 'running'"
             [class.am-step--failed]="step.status === 'failed'">
          <span class="am-step-status" [ngClass]="step.status" role="img" [attr.aria-label]="stepStatusLabel(step)">
            @switch (step.status) {
              @case ('success') { <i class="fa-solid fa-check" aria-hidden="true"></i> }
              @case ('failed') { <i class="fa-solid fa-xmark" aria-hidden="true"></i> }
              @case ('running') { <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> }
              @case ('stopped') { <i class="fa-solid fa-stop" aria-hidden="true"></i> }
              @case ('not-run') { <i class="fa-solid fa-circle-minus" aria-hidden="true"></i> }
              @case ('skipped') { <i class="fa-solid fa-forward" aria-hidden="true"></i> }
              @default { <i class="fa-solid fa-circle-dot" aria-hidden="true"></i> }
            }
          </span>
          <!-- The number rides with its caption, inside the body (XAT-E-526-05): a fourth child of the row was a fourth
               cell to the window's three-area grid, which placed it under the time. The failed line sits under the
               caption's text, not under the number. -->
          <span class="am-step-body">
            <span class="am-step-line">
              <span class="am-step-number">{{ index + 1 }}</span>
              <span class="am-step-text">
                <span class="am-step-caption">{{ readerCaption(step) }}</span>
                @if (step.status === 'failed') {
                  <span class="am-step-plain-error">{{ readerStepFailed }}</span>
                }
              </span>
            </span>
          </span>
          <span class="am-step-duration">{{ readerDuration(step.durationMs) }}</span>
        </div>
      } @empty {
        <div class="am-step am-step-empty">Waiting for the first step…</div>
      }
    } @else {
    @for (group of groups; track trackGroup($index, group)) {
      @if (hasGroupHeaders) {
        <div class="am-group">
          <span class="am-group-index">Batch {{ group.index }}</span>
          @if (group.summary) {
            <span class="am-group-summary">{{ group.summary }}</span>
          }
        </div>
      }

      <!-- A CALL RENDERS AS A FRAME (XAT-E-291-06). The compiler splices an inlined script's steps into
           this same flat list between a `call` and a `return`, so the grouping is derived from the two
           kinds already here — no second shape, and a run with no call in it renders exactly as before. -->
      @for (row of rowsIn(group); track trackRow($index, row)) {
        @if (row.kind === 'frame') {
          <button type="button"
                  class="am-frame"
                  [ngClass]="row.frame.status"
                  [style.padding-left.px]="12 + indentOf(row)"
                  [attr.aria-expanded]="!isFrameCollapsed(row.frame)"
                  [title]="'The steps of ' + row.frame.script"
                  (click)="toggleFrame(row.frame)">
            <i class="fa-solid"
               [class.fa-chevron-down]="!isFrameCollapsed(row.frame)"
               [class.fa-chevron-right]="isFrameCollapsed(row.frame)"
               aria-hidden="true"></i>
            <span class="am-frame-title">{{ row.frame.title }}</span>
            <span class="am-frame-count">{{ frameCount(row.frame) }}</span>
          </button>
        } @else {
          <div class="am-step"
               [style.padding-left.px]="12 + indentOf(row)"
               [class.dim]="isDim(row.step)"
               [class.am-step--running]="row.step.status === 'running'"
               [class.am-step--failed]="row.step.status === 'failed'">
            <!-- ONE GLYPH PER STATUS, AND THE STATUS WORD BEHIND IT (XAT-E-265-03). The glyph was already
                 per-status with the colour on top, so nothing here ever meant anything by hue alone — what
                 was missing was the NAME: this cell is the row's only statement of how the step ended, and
                 a screen reader got either silence or a private-use codepoint. `pending` was also the one
                 outline glyph in an otherwise solid panel; fa-circle-dot says the same "not started" in the
                 900 weight the other six are drawn in, and fa-spin is the spin the Playground's strip and
                 the Uploaded files panel turn with. -->
            <span class="am-step-status" [ngClass]="row.step.status" role="img"
                  [attr.aria-label]="stepStatusLabel(row.step)">
              @switch (row.step.status) {
                @case ('success') { <i class="fa-solid fa-check" aria-hidden="true"></i> }
                @case ('failed') { <i class="fa-solid fa-xmark" aria-hidden="true"></i> }
                @case ('running') { <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i> }
                @case ('stopped') { <i class="fa-solid fa-stop" aria-hidden="true"></i> }
                <!-- fa-circle-minus, not fa-minus (XAT-E-265-03): a bare dash is the Minimise button's
                     glyph, forty pixels up this same panel. The circled minus is what the Playground's
                     own run strip draws for "nothing happened here", which is exactly what this is. -->
                @case ('not-run') { <i class="fa-solid fa-circle-minus" aria-hidden="true"></i> }
                @case ('skipped') { <i class="fa-solid fa-forward" aria-hidden="true"></i> }
                @default { <i class="fa-solid fa-circle-dot" aria-hidden="true"></i> }
              }
            </span>
            <span class="am-step-ordinal" [title]="row.step.key">s{{ row.step.ordinal }}</span>
            <span class="am-step-badge" [ngClass]="badgeClass(row.step)" [title]="badgeLabel(row.step)">{{ badgeLabel(row.step) }}</span>
            <span class="am-step-body">
              <span class="am-step-desc"
                    [class.am-step-desc--expected]="row.step.expectation"
                    [title]="headline(row.step)">{{ headline(row.step) }}</span>
              <!-- A LOOP'S ROW SAYS WHICH PASS IT IS (XCT-E-14-07): the trail keeps only a body's latest pass, so
                   a body step is one row with the pass of every loop around it, and a forEach with its own. -->
              @if (passesOf(row.step).length) {
                <span class="am-step-passes">
                  @for (loop of passesOf(row.step); track loop.scriptStep) {
                    <span class="am-step-pass" [title]="passTitle(loop)">{{ passLabel(loop) }}</span>
                  }
                </span>
              }
              <!-- A background run's row (XAT-E-255-01) says what it dispatched and where, under what it was
                   for; a browser action carries no command and shows nothing here. -->
              @if (row.step.command) {
                <span class="am-step-command" [title]="row.step.command">{{ row.step.command }}</span>
              }
              @if (row.step.error) {
                <span class="am-step-error" [title]="row.step.error">{{ row.step.error }}</span>
              }
              <!-- A Db WRITE STEP'S ROWS (XAT-E-336-07): one line per record an update staged, one per
                   command a commit dispatched - and on a command that could not write, the rows it did
                   not - under the step's own line, the way the Playground lists them under the step. -->
              @for (line of row.step.details ?? []; track $index) {
                <span class="am-step-detail" [title]="line">{{ line }}</span>
              }
              @if (row.step.status === 'not-run') {
                <span class="am-step-note">not run</span>
              }
              @if (row.step.status === 'skipped') {
                <span class="am-step-note">skipped</span>
              }
            </span>
            <span class="am-step-duration">{{ formatDuration(row.step.durationMs) }}</span>
          </div>
        }
      }
    } @empty {
      <div class="am-step am-step-empty">Waiting for the first action…</div>
    }
    }
  </div>

  <!-- Footer: the outcome, then what can be done with the run — Stop while it is going, Re-run and
       Copy log once it has reported. -->
  <!-- WHICH KIND OF FAILURE (XCT-E-91-01): beside the status as a label, and on the footer as data-failure-kind, the one
       place a test reads it — so a negative test proves the right thing failed without parsing the sentence. -->
  <div class="am-footer"
       [class.ok]="run.status === 'completed'"
       [class.bad]="run.status === 'failed'"
       [class.halted]="run.status === 'stopped' || run.status === 'timed-out'"
       [attr.data-failure-kind]="failureKind">
    @if (failureKind && !reader) {
      <span class="am-footer-kind" data-cy="txt-monitor-failure-kind" [title]="'This run failed: ' + failureKind">Failed · {{ failureKind }}</span>
    }
    <span class="am-footer-text">{{ footerText }}</span>

    <!-- `canStop` alone decides: every run that can be stopped says so while it runs, and a BACKGROUND
         run (XAT-E-255) says it cannot — the server runs it, and a Stop that reached this tab's loop
         would do nothing while looking like it had. -->
    @if (run.canStop) {
      <button type="button" class="am-stop" data-cy="btn-monitor-stop" (click)="stop()" title="Stop this run">
        <i class="fa-solid fa-stop" aria-hidden="true"></i> Stop
      </button>
    }

    @if (canOfferRerun && offersRunActions) {
      <button type="button"
              class="am-action"
              data-cy="btn-monitor-rerun"
              [disabled]="!rerunState.enabled"
              [title]="rerunState.reason || 'Run the same code again'"
              (click)="rerun()">
        <i class="fa-solid fa-rotate-right" aria-hidden="true"></i> Re-run
      </button>
    }

    @if (!isRunning && offersRunActions) {
      <button type="button"
              class="am-action"
              data-cy="btn-monitor-copy-log"
              [title]="copied ? 'Copied' : 'Copy this run as plain text'"
              [attr.aria-label]="copied ? 'Copied' : 'Copy this run as plain text'"
              (click)="copyLog()">
        <i class="fa-solid" [class.fa-copy]="!copied" [class.fa-check]="copied" aria-hidden="true"></i>
        {{ copied ? 'Copied' : 'Copy log' }}
      </button>
    }
  </div>
}
